import { useCallback, useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { AdminUserPanel } from "@/components/AdminUserPanel";

import { fmtDate, place, usd } from "@/lib/format";
import { CreditsBar } from "@/components/CreditsBar";

const Stat = ({ label, value, testid }) => (
  <div className="border-t border-[var(--rf-ink)] pt-3">
    <p className="text-3xl font-black tracking-tighter" data-testid={testid}>{value}</p>
    <p className="rf-mono text-[10px] tracking-[0.2em] uppercase text-[var(--rf-grey)] mt-1">{label}</p>
  </div>
);

export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return users.filter((u) => !s || [u.email, u.name, u.source?.label, place(u.geo), u.last_ip, u.signup_ip].join(" ").toLowerCase().includes(s));
  }, [users, q]);

  const week = Date.now() - 7 * 864e5;
  const ips = new Set(users.flatMap((u) => u.ips || []).filter(Boolean));
  const current = users.find((u) => u.id === selected);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-[1400px] mx-auto px-5 sm:px-10 py-10 space-y-10">
        <div className="space-y-3">
          <p className="rf-mono text-[11px] tracking-[0.3em] uppercase rf-red">Admin</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">Iscritti</h1>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          <Stat label="Iscritti totali" value={users.length} testid="admin-stat-total" />
          <Stat label="Nuovi ultimi 7 giorni" value={users.filter((u) => new Date(u.created_at) > week).length} testid="admin-stat-new" />
          <Stat label="IP unici" value={ips.size} testid="admin-stat-ips" />
          <Stat label="Messaggi non letti" value={users.reduce((a, u) => a + u.unread, 0)} testid="admin-stat-unread" />
        </div>
        <CreditsBar />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-10 gap-y-10">
          <section className="min-w-0 lg:col-span-7">
            <label className="flex items-center gap-3 border-b border-[var(--rf-ink)] pb-2">
              <Search size={15} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per email, provenienza, città, IP…"
                className="flex-1 outline-none text-sm bg-transparent" data-testid="admin-search-input" />
            </label>
            <ul data-testid="admin-users-list">
              {filtered.map((u) => (
                <li key={u.id}>
                  <button onClick={() => setSelected(u.id)} data-testid={`admin-user-row-${u.id}`}
                    className={`w-full text-left grid grid-cols-12 gap-3 py-4 px-2 border-b rf-line transition-colors ${selected === u.id ? "bg-[var(--rf-soft)]" : "hover:bg-[var(--rf-soft)]"}`}>
                    <span className="col-span-12 sm:col-span-5 min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-semibold truncate">{u.email}</span>
                        {u.role === "admin" && <span className="rf-mono text-[9px] uppercase rf-red">admin</span>}
                        {u.unread > 0 && <span className="bg-[var(--rf-red)] text-white rf-mono text-[10px] px-1.5" data-testid={`admin-user-unread-${u.id}`}>{u.unread}</span>}
                      </span>
                      <span className="block rf-mono text-[10px] text-[var(--rf-grey)] truncate">iscritto {fmtDate(u.created_at)} · {u.provider}</span>
                    </span>
                    <span className="col-span-6 sm:col-span-3 text-xs min-w-0">
                      <span className="block truncate font-medium">{u.source?.label || "—"}</span>
                      <span className="block truncate text-[var(--rf-grey)]">{place(u.geo)}</span>
                    </span>
                    <span className="col-span-4 sm:col-span-3 text-xs min-w-0">
                      <span className="block truncate">{u.device || "—"}</span>
                      <span className="block truncate text-[var(--rf-grey)]">{fmtDate(u.last_seen)}</span>
                    </span>
                    <span className="col-span-2 sm:col-span-1 text-right">
                      <span className="block text-lg font-black leading-none">{u.analyses}</span>
                      <span className="rf-mono text-[9px] uppercase text-[var(--rf-grey)]">analisi</span>
                      <span className="block rf-mono text-[10px] rf-red mt-1" data-testid={`admin-user-cost-${u.id}`}>{usd(u.cost, 3)}</span>
                    </span>
                  </button>
                </li>
              ))}
              {filtered.length === 0 && <li className="py-6 text-sm text-[var(--rf-grey)]" data-testid="admin-users-empty">Nessun iscritto trovato.</li>}
            </ul>
          </section>
          <section className="min-w-0 lg:col-span-5">
            {current ? <AdminUserPanel user={current} onRead={load} /> : (
              <p className="text-sm text-[var(--rf-grey)] border-t border-[var(--rf-ink)] pt-4" data-testid="admin-no-selection">Seleziona un iscritto per vedere i dettagli e chattare.</p>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
